import { ICopyable } from 'src/app/controls/common/icopyable';
import { ContextOfUse } from '../Enums/context-of-use';
import { IRestApiConfiguration } from '../Interfaces/rest-api-configuration.interface';
import { RestApiEventConfiguration } from './rest-api-event-configuration';
import { RestApiRequestConfiguration } from './rest-api-request-configuration';
import { RestApiResponseConfiguration } from './rest-api-response-configuration';

export class RestApiConfiguration implements ICopyable<RestApiConfiguration>, IRestApiConfiguration {
    public id?: number;
    public organizationId: string;
    public viewId: number;
    public name: string;
    public description: string;
    public contextOfUse: ContextOfUse;
    public updateDate: Date;
    public requestConfiguration: RestApiRequestConfiguration = new RestApiRequestConfiguration();
    public responseConfiguration: RestApiResponseConfiguration = new RestApiResponseConfiguration();
    public eventConfigurations: RestApiEventConfiguration[] = [];
    public timeout: number = 30;

    public constructor() {}

    public createCopy(): RestApiConfiguration {
        const instance = new RestApiConfiguration();
        instance.id = this.id;
        instance.organizationId = this.organizationId;
        instance.viewId = this.viewId;
        instance.name = this.name;
        instance.description = this.description;
        instance.contextOfUse = this.contextOfUse;
        instance.updateDate = this.updateDate;
        instance.requestConfiguration = RestApiRequestConfiguration.clone(this.requestConfiguration);
        instance.responseConfiguration = RestApiResponseConfiguration.clone(this.responseConfiguration);
        instance.eventConfigurations = RestApiEventConfiguration.cloneArray(this.eventConfigurations);
        instance.timeout = this.timeout;

        return instance;
    }

    public static createFrom(other: any): RestApiConfiguration {
        const instance = new RestApiConfiguration();
        instance.id = other.id;
        instance.organizationId = other.organizationId;
        instance.viewId = other.viewId;
        instance.name = other.name;
        instance.description = other.description;
        instance.contextOfUse = other.contextOfUse;
        instance.updateDate = other.updateDate;
        instance.requestConfiguration = other.requestConfiguration;
        instance.responseConfiguration = other.responseCongiguration;
        instance.eventConfigurations = other.eventConfigurations;
        instance.timeout = other.timeout;

        return instance;
    }

    public static createFromForm(restApiConfiguration: IRestApiConfiguration): RestApiConfiguration {
        const instance = new RestApiConfiguration();
        instance.id = restApiConfiguration.id;
        instance.organizationId = restApiConfiguration.organizationId;
        instance.viewId = restApiConfiguration.viewId;
        instance.name = restApiConfiguration.name;
        instance.description = restApiConfiguration.description;
        instance.contextOfUse = restApiConfiguration.contextOfUse;
        instance.updateDate = restApiConfiguration.updateDate;
        instance.requestConfiguration = restApiConfiguration.requestConfiguration;
        instance.responseConfiguration = restApiConfiguration.responseConfiguration;
        instance.eventConfigurations = restApiConfiguration.eventConfigurations;
        instance.timeout = restApiConfiguration.timeout;

        return instance;
    }

    public static createFormCopy(restApiConfiguration: RestApiConfiguration): RestApiConfiguration {
        const instance = new RestApiConfiguration();
        instance.id = restApiConfiguration.id;
        instance.organizationId = restApiConfiguration.organizationId;
        instance.viewId = restApiConfiguration.viewId;
        instance.name = restApiConfiguration.name;
        instance.description = restApiConfiguration.description;
        instance.contextOfUse = restApiConfiguration.contextOfUse;
        instance.updateDate = restApiConfiguration.updateDate;
        instance.requestConfiguration = restApiConfiguration.requestConfiguration;
        instance.responseConfiguration = restApiConfiguration.responseConfiguration;
        instance.eventConfigurations = restApiConfiguration.eventConfigurations;
        instance.timeout = restApiConfiguration.timeout;

        return instance;
    }

    public copyFrom(other: any) {
        this.id = other.id;
        this.organizationId = other.organizationId;
        this.viewId = other.viewId;
        this.name = other.name;
        this.description = other.description;
        this.contextOfUse = other.contextOfUse;
        this.updateDate = other.updateDate;
        this.requestConfiguration = other.requestConfiguration;
        this.responseConfiguration = other.responseCongiguration;
        this.eventConfigurations = other.eventConfigurations;
        this.timeout = other.timeout;
    }

    /**
     * Retourne une instance de RestApiConfiguration à partir d'une instance de IRestApiConfiguration.
     * @param other : Instance de IRestApiConfiguration.
     * @returns : Instance de RestApiConfiguration.
     */
    public static from(other: IRestApiConfiguration) {
        if (!!other) {
            const instance = new RestApiConfiguration();

            instance.id = other.id;
            instance.organizationId = other.organizationId;
            instance.viewId = other.viewId;
            instance.name = other.name;
            instance.description = other.description;
            instance.contextOfUse = other.contextOfUse;
            instance.updateDate = other.updateDate;
            instance.requestConfiguration = other.requestConfiguration;
            instance.responseConfiguration = other.responseConfiguration;
            instance.eventConfigurations = other.eventConfigurations;
            instance.timeout = other.timeout;

            return instance;
        } else {
            return null;
        }
    }

    /**
     * Retourne un tableau d'instances de RestApiConfiguration à partir d'un tableau d'instances de IRestApiConfiguration.
     * @param other : Tableau d'instances de IRestApiConfiguration.
     * @returns : Tableau d'instances de RestApiConfiguration.
     */
    public static fromArray(others: IRestApiConfiguration[]) {
        if (!!others) {
            let triggers: RestApiConfiguration[] = [];

            others.forEach((x) => {
                const trigger = RestApiConfiguration.from(x);
                triggers.push(trigger);
            });

            return triggers;
        } else {
            return [];
        }
    }

    public getDisplayName() {
        return `${this.name}`;
    }

    /**
     * Retourne une copie d'une instance de RestApiConfiguration
     * @param other : Instance source.
     * @returns : Copie de l'instance.
     */
    public static clone(other: RestApiConfiguration) {
        if (!!other) {
            const instance = new RestApiConfiguration();

            instance.id = other.id;
            instance.organizationId = other.organizationId;
            instance.viewId = other.viewId;
            instance.name = other.name;
            instance.description = other.description;
            instance.contextOfUse = other.contextOfUse;
            instance.updateDate = other.updateDate;
            instance.requestConfiguration = RestApiRequestConfiguration.clone(other.requestConfiguration);
            instance.responseConfiguration = RestApiResponseConfiguration.clone(other.responseConfiguration);
            instance.eventConfigurations = RestApiEventConfiguration.cloneArray(other.eventConfigurations);
            instance.timeout = other.timeout;

            return instance;
        } else {
            return null;
        }
    }

    /**
     * Retourne une copie d'un tableau d'instances de RestApiConfiguration
     * @param other : Tableau source.
     * @returns : Copie du tableau.
     */
    public static cloneArray(others: RestApiConfiguration[]) {
        if (!!others) {
            let configurations: RestApiConfiguration[] = [];

            others.forEach((x) => {
                const configuration = RestApiConfiguration.clone(x);
                configurations.push(configuration);
            });

            return configurations;
        } else {
            return [];
        }
    }
}
